import React, { useMemo, useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { alpha } from "@mui/material/styles";
import { Box, Stack, Tooltip, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EventSeatIcon from "@mui/icons-material/EventSeat";

const SEAT_MAX = 50;
const SEAT_MIN = 26;
const SEAT_GAP = 6; // gap 0.75 * 8px
const COL_GAP = 6;
const AISLE_H = 14;

/** Volante estilizado: comunica a frente do veículo / posição do motorista. */
function SteeringWheel({ size = 22 }) {
  return (
    <Box
      component="svg"
      viewBox="0 0 24 24"
      sx={{ width: size, height: size, display: "block", color: "text.secondary" }}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      aria-hidden
    >
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="2.4" fill="currentColor" stroke="none" />
      <line x1="12" y1="9.6" x2="12" y2="3.2" />
      <line x1="10.1" y1="13.3" x2="5" y2="17.6" />
      <line x1="13.9" y1="13.3" x2="19" y2="17.6" />
    </Box>
  );
}

function deckLabelFor(index, t) {
  if (index === 0) return t("bpeWizard.seatMapDeckLower");
  if (index === 1) return t("bpeWizard.seatMapDeckUpper");
  return t("bpeWizard.seatMapDeckN", { n: index + 1 });
}

function asInt(v) {
  const n = typeof v === "number" ? v : parseInt(String(v), 10);
  return Number.isFinite(n) ? n : null;
}

function uniqSorted(nums) {
  return Array.from(new Set(nums)).sort((a, b) => a - b);
}

function pickAisleIndex(uniqueYs) {
  if (uniqueYs.length <= 1) return 0;
  let bestGapIdx = null;
  let bestGap = 1;
  for (let i = 0; i < uniqueYs.length - 1; i++) {
    const gap = uniqueYs[i + 1] - uniqueYs[i];
    if (gap > bestGap) {
      bestGap = gap;
      bestGapIdx = i;
    }
  }
  if (bestGapIdx != null && bestGap >= 2) return bestGapIdx;
  if (uniqueYs.length === 4) return 1;
  if (uniqueYs.length === 6) return 2;
  return Math.max(0, Math.floor((uniqueYs.length - 1) / 2));
}

function LegendSwatch({ children, label, sx, compact }) {
  const swatch = (
    <Box
      sx={{
        width: compact ? 22 : 30,
        height: compact ? 22 : 30,
        borderRadius: 0.75,
        display: "grid",
        placeItems: "center",
        flexShrink: 0,
        ...sx,
      }}
    >
      {children}
    </Box>
  );

  if (compact && label) {
    return (
      <Tooltip title={label} arrow>
        {swatch}
      </Tooltip>
    );
  }

  return (
    <Stack direction="row" spacing={0.75} alignItems="center">
      {swatch}
      <Typography variant="caption" color="text.secondary" fontWeight={600}>
        {label}
      </Typography>
    </Stack>
  );
}

/**
 * Mapa visual de poltronas (coordenadas do parceiro ou grade sintética / chips).
 */
const BPeSeatMap = ({
  seats = [],
  availableSeatNumbers = [],
  selectedByPassenger = {},
  activePassengerIndex = 0,
  passengerCount = 1,
  onSelectSeat,
  compactHeader = false,
}) => {
  const { t } = useTranslation("chat");

  const availability = useMemo(() => {
    const set = new Set();
    for (const v of availableSeatNumbers || []) {
      const n = asInt(v);
      if (n != null) set.add(String(n));
    }
    return set;
  }, [availableSeatNumbers]);

  const seatOwnerByNumber = useMemo(() => {
    const map = new Map();
    Object.entries(selectedByPassenger || {}).forEach(([idx, seat]) => {
      const n = asInt(seat);
      if (n != null) map.set(String(n), Number(idx));
    });
    return map;
  }, [selectedByPassenger]);

  const decks = useMemo(() => {
    const withPos = (seats || [])
      .map((s) => ({
        ...s,
        x: typeof s.x === "number" ? s.x : null,
        y: typeof s.y === "number" ? s.y : null,
        numero: asInt(s.numero),
        piso: s.piso == null || s.piso === "" ? null : String(s.piso),
      }))
      .filter((s) => s.x != null && s.y != null);

    const groups = new Map();
    for (const s of withPos) {
      const key = s.piso == null ? "__single__" : s.piso;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(s);
    }

    // O piso com a menor poltrona é a frente do veículo (deck inferior / com motorista).
    const minNumero = (arr) =>
      arr.reduce((m, s) => (s.numero != null && s.numero < m ? s.numero : m), Infinity);
    const keys = [...groups.keys()].sort((a, b) => minNumero(groups.get(a)) - minNumero(groups.get(b)));

    const list = keys.map((key, index) => {
      const arr = groups.get(key);
      const xs = uniqSorted(arr.map((s) => s.x));
      const ys = uniqSorted(arr.map((s) => s.y));
      const byXY = new Map();
      for (const s of arr) byXY.set(`${s.x}:${s.y}`, s);
      return { key, index, xs, ys, byXY, aisleAfter: pickAisleIndex(ys) };
    });

    return { list, withPosCount: withPos.length };
  }, [seats]);

  const hasCoordLayout = decks.withPosCount >= Math.min(8, (seats || []).length);
  const multiDeck = decks.list.length > 1;
  const hasAvailList = availability.size > 0;

  const frontCapWidth = compactHeader ? 30 : 38;

  // Dimensão das poltronas: por padrão fixa, mas em modo compacto (wizard) escala
  // para caber na área disponível sem precisar de rolagem.
  const containerRef = useRef(null);
  const headerRef = useRef(null);
  const [seatSize, setSeatSize] = useState(SEAT_MAX);

  const layoutMetrics = useMemo(() => {
    let totalRows = 0;
    let heightOverhead = 0;
    let maxCols = 1;
    for (const d of decks.list) {
      const rows = d.ys.length;
      totalRows += rows;
      // aïsle (linha pontilhada) + gaps entre poltronas + rótulo do piso (multideck)
      heightOverhead += AISLE_H + rows * SEAT_GAP + (multiDeck ? 22 : 0);
      maxCols = Math.max(maxCols, d.xs.length);
    }
    if (multiDeck) heightOverhead += (decks.list.length - 1) * 14;
    const widthOverhead = frontCapWidth + 12 + maxCols * COL_GAP;
    return {
      totalRows: Math.max(1, totalRows),
      maxCols,
      heightOverhead,
      widthOverhead,
    };
  }, [decks.list, multiDeck, frontCapWidth]);

  useEffect(() => {
    if (!compactHeader) {
      setSeatSize(SEAT_MAX);
      return undefined;
    }
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;

    const recompute = () => {
      const cw = el.clientWidth;
      const ch = el.clientHeight;
      if (!cw || !ch) return;
      const headerH = headerRef.current?.offsetHeight || 0;
      const padX = 20; // p:1.25 -> ~10px * 2
      const padY = 20;
      const availW = cw - padX;
      const availH = ch - headerH - padY - 8;
      const byH = (availH - layoutMetrics.heightOverhead) / layoutMetrics.totalRows;
      const byW = (availW - layoutMetrics.widthOverhead) / layoutMetrics.maxCols;
      const next = Math.floor(Math.min(byH, byW));
      if (Number.isFinite(next)) {
        setSeatSize(Math.max(SEAT_MIN, Math.min(SEAT_MAX, next)));
      }
    };

    recompute();
    const ro = new ResizeObserver(recompute);
    ro.observe(el);
    return () => ro.disconnect();
  }, [compactHeader, layoutMetrics]);

  const seatFontSize = seatSize >= 42 ? "0.85rem" : seatSize >= 34 ? "0.75rem" : "0.65rem";

  const getSeatState = (n, apiFree) => {
    const owner = seatOwnerByNumber.get(String(n));
    if (owner != null && owner === activePassengerIndex) return "selected_active";
    if (owner != null) return "selected_other";
    const isAvailable = hasAvailList ? availability.has(String(n)) : apiFree;
    if (!isAvailable) return "occupied";
    return "available";
  };

  const renderSeatCell = (n, state) => {
    const label = String(n).padStart(2, "0");
    const owner = seatOwnerByNumber.get(String(n));
    const tooltip =
      state === "occupied"
        ? t("bpeWizard.seatTooltipOccupied", { seat: n })
        : state === "selected_active"
          ? t("bpeWizard.seatTooltipSelected", { seat: n })
          : state === "selected_other"
            ? t("bpeWizard.seatTooltipOtherPassenger", { seat: n, passenger: (owner ?? 0) + 1 })
            : t("bpeWizard.seatTooltipAvailable", { seat: n });

    const baseSx = {
      width: seatSize,
      height: seatSize,
      borderRadius: 1.5,
      display: "grid",
      placeItems: "center",
      fontWeight: 800,
      fontSize: seatFontSize,
      transition: "transform 0.12s ease, box-shadow 0.12s ease, border-color 0.12s ease",
      userSelect: "none",
    };

    if (state === "occupied") {
      return (
        <Tooltip key={n} title={tooltip} arrow>
          <Box
            sx={{
              ...baseSx,
              bgcolor: (theme) => alpha(theme.palette.grey[500], 0.18),
              color: "text.disabled",
              border: 1,
              borderColor: (theme) => alpha(theme.palette.grey[500], 0.35),
              position: "relative",
            }}
            aria-label={tooltip}
          >
            <Typography
              component="span"
              sx={{
                fontWeight: 800,
                fontSize: "0.8rem",
                textDecoration: "line-through",
                opacity: 0.55,
              }}
            >
              {label}
            </Typography>
            <CloseIcon
              sx={{
                position: "absolute",
                top: 2,
                right: 2,
                fontSize: 12,
                opacity: 0.45,
              }}
            />
          </Box>
        </Tooltip>
      );
    }

    const isClickable = state === "available" || state === "selected_active";
    const isActive = state === "selected_active";

    return (
      <Tooltip key={n} title={tooltip} arrow>
        <Box
          component="button"
          type="button"
          disabled={!isClickable}
          onClick={() => isClickable && onSelectSeat(activePassengerIndex, n)}
          aria-label={tooltip}
          aria-pressed={isActive}
          sx={{
            ...baseSx,
            p: 0,
            cursor: isClickable ? "pointer" : "default",
            border: 2,
            borderColor:
              state === "selected_other"
                ? "warning.main"
                : isActive
                  ? "primary.main"
                  : "success.main",
            bgcolor: isActive
              ? "primary.main"
              : state === "selected_other"
                ? (theme) => alpha(theme.palette.warning.main, 0.15)
                : "background.paper",
            color: isActive ? "primary.contrastText" : "text.primary",
            boxShadow: isActive ? 4 : state === "available" ? 1 : 0,
            position: "relative",
            "&:hover": isClickable
              ? {
                  transform: "scale(1.08)",
                  boxShadow: 4,
                  zIndex: 1,
                  bgcolor: isActive ? "primary.dark" : (theme) => alpha(theme.palette.success.main, 0.08),
                }
              : {},
            "&:focus-visible": {
              outline: 3,
              outlineOffset: 2,
              outlineColor: "primary.main",
            },
          }}
        >
          {state === "selected_other" ? (
            <Typography
              component="span"
              variant="caption"
              sx={{
                position: "absolute",
                top: 2,
                right: 3,
                fontSize: "0.58rem",
                fontWeight: 800,
                color: "warning.dark",
                lineHeight: 1,
              }}
            >
              P{(owner ?? 0) + 1}
            </Typography>
          ) : null}
          {label}
        </Box>
      </Tooltip>
    );
  };

  const mapContainerSx = {
    border: 1,
    borderColor: "divider",
    borderRadius: 2,
    p: 1.25,
    bgcolor: (theme) => alpha(theme.palette.grey[500], 0.04),
    overflowX: "auto",
  };

  const renderFrontCap = (visible) => (
    <Box
      sx={{
        width: frontCapWidth,
        flexShrink: 0,
        alignSelf: "stretch",
        ...(visible
          ? {
              borderRadius: "6px 16px 16px 6px",
              border: 1,
              borderColor: "divider",
              bgcolor: (theme) => alpha(theme.palette.grey[700], 0.06),
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "flex-start",
              gap: 0.5,
              pt: 0.75,
            }
          : { visibility: "hidden" }),
      }}
      title={visible ? t("bpeWizard.seatMapDriver") : undefined}
      aria-label={visible ? t("bpeWizard.seatMapDriver") : undefined}
    >
      {visible ? (
        <>
          <SteeringWheel size={compactHeader ? 18 : 22} />
          <Typography
            variant="caption"
            sx={{
              writingMode: "vertical-rl",
              transform: "rotate(180deg)",
              fontSize: "0.55rem",
              fontWeight: 800,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "text.secondary",
              lineHeight: 1,
              mt: 0.25,
            }}
          >
            {t("bpeWizard.seatMapFront")}
          </Typography>
        </>
      ) : null}
    </Box>
  );

  const renderDeckGrid = (deck) => (
    <Box display="flex" gap={0.75} alignItems="flex-start">
      {[...deck.xs].reverse().map((x) => (
        <Box key={x} display="flex" flexDirection="column" gap={0.75} alignItems="center">
          {deck.ys.map((y, i) => {
            const showAisle = i === deck.aisleAfter + 1;
            const aisleEl = showAisle ? (
              <Box
                key={`aisle-${x}-${y}`}
                sx={{
                  width: seatSize,
                  height: 14,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderTop: 1,
                  borderBottom: 1,
                  borderStyle: "dashed",
                  borderColor: "divider",
                }}
                aria-hidden
              >
                {x === deck.xs[Math.floor(deck.xs.length / 2)] ? (
                  <Typography variant="caption" color="text.disabled" fontSize="0.5rem" fontWeight={700}>
                    {t("bpeWizard.seatMapAisle")}
                  </Typography>
                ) : null}
              </Box>
            ) : null;

            const s = deck.byXY.get(`${x}:${y}`);
            if (!s || s.numero == null) {
              return (
                <React.Fragment key={`${x}-${y}`}>
                  {aisleEl}
                  <Box sx={{ width: seatSize, height: seatSize }} />
                </React.Fragment>
              );
            }
            if (s.ocupado === 3 && s.feature) {
              return (
                <React.Fragment key={`${x}-${y}`}>
                  {aisleEl}
                  <Box
                    sx={{
                      width: seatSize,
                      height: seatSize,
                      display: "grid",
                      placeItems: "center",
                      fontSize: "0.55rem",
                      fontWeight: 800,
                      color: "text.secondary",
                      borderRadius: 1.5,
                      bgcolor: "action.hover",
                      border: 1,
                      borderColor: "divider",
                    }}
                    title={String(s.feature)}
                  >
                    {String(s.feature).slice(0, 4)}
                  </Box>
                </React.Fragment>
              );
            }

            const n = s.numero;
            const apiFree = s.ocupado === 0;
            const state = getSeatState(n, apiFree);
            return (
              <React.Fragment key={`${x}-${y}`}>
                {aisleEl}
                {renderSeatCell(n, state)}
              </React.Fragment>
            );
          })}
        </Box>
      ))}
    </Box>
  );

  if (!hasCoordLayout) {
    const list = (availableSeatNumbers || [])
      .map((v) => asInt(v))
      .filter((n) => n != null && !seatOwnerByNumber.has(String(n)));
    return (
      <Box sx={mapContainerSx}>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5, fontWeight: 600 }}>
          {t("bpeWizard.seatMapFallback")}
        </Typography>
        <Stack direction="row" flexWrap="wrap" gap={0.75}>
          {list.map((n) => renderSeatCell(n, getSeatState(n, true)))}
        </Stack>
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        ...mapContainerSx,
        flex: compactHeader ? 1 : undefined,
        display: compactHeader ? "flex" : "block",
        flexDirection: "column",
        minHeight: compactHeader ? 0 : undefined,
        // Em modo compacto as poltronas escalam para caber: sem rolagem vertical.
        ...(compactHeader ? { overflowY: "hidden" } : {}),
      }}
    >
      <Stack
        ref={headerRef}
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={0.75}
        sx={{
          mb: compactHeader ? 0.75 : 1,
          pb: compactHeader ? 0.5 : 1,
          borderBottom: 1,
          borderColor: "divider",
          flexShrink: 0,
        }}
      >
        <Typography variant="caption" color="text.secondary" fontWeight={700} lineHeight={1.2}>
          {t("bpeWizard.seatMapBack")} → {t("bpeWizard.seatMapFront")}
        </Typography>
        <Stack direction="row" flexWrap="wrap" gap={compactHeader ? 0.5 : 1.25}>
          <LegendSwatch
            compact={compactHeader}
            label={t("bpeWizard.seatAvailable")}
            sx={{
              border: 2,
              borderColor: "success.main",
              bgcolor: "background.paper",
            }}
          >
            <EventSeatIcon sx={{ fontSize: compactHeader ? 12 : 16, color: "success.main" }} />
          </LegendSwatch>
          <LegendSwatch
            compact={compactHeader}
            label={t("bpeWizard.seatUnavailable")}
            sx={{
              bgcolor: (theme) => alpha(theme.palette.grey[500], 0.18),
              border: 1,
              borderColor: (theme) => alpha(theme.palette.grey[500], 0.35),
            }}
          >
            <Typography
              variant="caption"
              fontWeight={800}
              sx={{ textDecoration: "line-through", opacity: 0.55, fontSize: "0.55rem" }}
            >
              01
            </Typography>
          </LegendSwatch>
          <LegendSwatch
            compact={compactHeader}
            label={t("bpeWizard.seatMapLegendSelected")}
            sx={{ bgcolor: "primary.main", color: "primary.contrastText" }}
          >
            <Typography variant="caption" fontWeight={800} sx={{ fontSize: "0.55rem" }}>
              34
            </Typography>
          </LegendSwatch>
          {passengerCount > 1 && (
            <LegendSwatch
              compact={compactHeader}
              label={t("bpeWizard.seatMapLegendOtherPassenger")}
              sx={{
                border: 2,
                borderColor: "warning.main",
                bgcolor: (theme) => alpha(theme.palette.warning.main, 0.15),
              }}
            >
              <Typography variant="caption" fontWeight={800} color="warning.dark" sx={{ fontSize: "0.55rem" }}>
                P2
              </Typography>
            </LegendSwatch>
          )}
        </Stack>
      </Stack>

      <Stack
        spacing={multiDeck ? 1.75 : 0}
        sx={{
          flex: compactHeader ? 1 : undefined,
          minHeight: 0,
          alignItems: compactHeader && !multiDeck ? "center" : "flex-start",
        }}
      >
        {decks.list.map((deck) => (
          <Box key={deck.key} sx={{ width: multiDeck ? "100%" : undefined }}>
            {multiDeck ? (
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={800}
                sx={{ display: "block", mb: 0.5, textTransform: "uppercase", letterSpacing: "0.04em" }}
              >
                {deckLabelFor(deck.index, t)}
              </Typography>
            ) : null}
            <Box
              display="flex"
              gap={1.5}
              alignItems="stretch"
              sx={{ justifyContent: compactHeader && !multiDeck ? "center" : "flex-start" }}
            >
              {renderDeckGrid(deck)}
              {renderFrontCap(deck.index === 0)}
            </Box>
          </Box>
        ))}
      </Stack>
    </Box>
  );
};

export default BPeSeatMap;
